import { useTranslation } from 'react-i18next';
import { Scan, Cloud, ShieldCheck, Plug, Lock, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FeatureItemProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  isReversed?: boolean;
  featureId: string;
  image?: string;
}

const FeatureItem = ({ title, description, icon, isReversed = false, featureId, image }: FeatureItemProps) => {
  const { t } = useTranslation();
  
  return (
    <div
      className={cn(
        "group flex flex-col md:flex-row items-center gap-6 lg:gap-10 py-6 md:py-8",
        "border-t border-gray-200 dark:border-gray-800 first:border-t-0",
        isReversed ? 'md:flex-row-reverse' : ''
      )}
    >
      <div className="md:w-[45%] lg:w-1/2">
        <div className="flex items-center gap-3.5 mb-4">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white"
            style={{
              background:
                'linear-gradient(135deg, rgba(0,37,73,1) 0%, rgba(12,67,120,1) 100%)',
            }}
          >
            {icon}
          </div>
          <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            {title}
          </h3>
        </div>
        <p className="text-gray-600 dark:text-gray-300 text-base md:text-[17px] leading-relaxed mb-4 md:mb-5">
          {description}
        </p>
        <div className="flex items-center gap-4 text-sm">
          <a
            href={`/features/${featureId}`}
            className="inline-flex items-center gap-1.5 text-primary-700 dark:text-primary-300 font-medium"
          >
            <span className="underline underline-offset-4 decoration-2 decoration-primary-300/50 hover:decoration-primary-500 transition-colors">
              {t('common.learnMore')}
            </span>
            <svg
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>

        </div>
      </div>
      <div className="md:w-1/2">
        <div className="relative rounded-xl overflow-hidden aspect-video">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent dark:from-black/20" />
        </div>
      </div>
    </div>
  );
};

export const WhyChooseUs = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Scan className="w-8 h-8" />,
      title: t('whyChooseUs.features.accuracy.title'),
      description: t('whyChooseUs.features.accuracy.description'),
      featureId: 'accuracy',
      image: '/why-choose-us/Detection_IA.png',
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: t('whyChooseUs.features.technology.title'),
      description: t('whyChooseUs.features.technology.description'),
      featureId: 'technology',
      image: '/why-choose-us/Architecture_Cloud.png',
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: t('whyChooseUs.features.expertise.title'),
      description: t('whyChooseUs.features.expertise.description'),
      featureId: 'expertise',
      image: '/why-choose-us/expertise.png',
    },
    {
      icon: <Plug className="w-8 h-8" />,
      title: t('whyChooseUs.features.support.title'),
      description: t('whyChooseUs.features.support.description'),
      featureId: 'support',
      image: '/why-choose-us/Integration_Installation.png',
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: t('whyChooseUs.features.authentication.title'),
      description: t('whyChooseUs.features.authentication.description'),
      featureId: 'authentication',
      image: '/why-choose-us/Authentification.png',
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: t('whyChooseUs.features.monitoring.title'),
      description: t('whyChooseUs.features.monitoring.description'),
      featureId: 'monitoring',
      image: '/why-choose-us/Surveillance.png',
    },
  ];

  return (
    <section
      id="why-choose-us"
      className="relative py-10 md:py-14 bg-white dark:bg-gray-900"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-3">
            {t('whyChooseUs.title')}
          </h2>
          <p className="text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('whyChooseUs.subtitle')}
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
              isReversed={index % 2 !== 0}
              featureId={feature.featureId}
              image={feature.image}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
